import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = "The Introvert's Guide to Support Raising (Without Pretending to Be Someone Else)";
const DESCRIPTION =
    'A practical approach to raising missionary support if small talk drains you and cold outreach feels impossible — built around written communication and one-on-one conversations instead of a performance.';

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover27.jpg',
            datePublished: '2026-07-27',
            dateModified: '2026-07-27',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/introverts-guide-to-support-raising',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Can an introvert really raise full missionary support?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Yes — support raising rewards clarity and follow-through far more than extroverted energy. Most giving decisions happen after someone reads a letter or has one quiet, focused conversation, not because they were won over by a room full of small talk.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Do I have to do phone calls to raise support?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "No. A well-written letter or email that clearly explains your calling, your need, and how to give converts plenty of people on its own. Reserve calls for the handful of relationships where a conversation is genuinely warranted, and let writing carry the bulk of the work.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How do I ask for support without doing a big group presentation?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'One-on-one meetings, or even just a direct letter with a clear next step, work as well as a group presentation for most individual donors. A group setting is efficient for reaching many people at once, but it is not required — you can build full support entirely through individual asks if that suits you better.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is it okay to let my support page do most of the talking?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes. A clear support page that explains your story and makes giving effortless does real persuasive work on its own, especially for recurring donors who prefer to read and decide in their own time rather than respond to pressure in the moment.",
                    },
                },
            ],
        },
    ],
};

export default function IntrovertsGuideToSupportRaising() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/introverts-guide-to-support-raising' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-introverts-guide-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover27.jpg" alt="A person writing a letter alone at a quiet desk, representing a low-key approach to support raising" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Most support-raising advice is written by extroverts, for extroverts: fill a room, work the phone, "just be yourself" at a networking event. If that description makes your stomach drop, you're not bad at this — you've just been handed the wrong playbook. Support raising can be done almost entirely through writing and quiet one-on-one conversations, and it works just as well.</p>

                    <h2>The math doesn't require a big personality</h2>

                    <p>Full support is usually built from a relatively small number of committed monthly donors, not a crowd won over in a single room. If you need forty supporters, that's forty individual decisions, made privately, almost always after someone reads something you wrote or has one direct conversation with you — not after a performance. Nobody signs up for recurring giving because you were the loudest person at an event. They sign up because your letter was clear, your ask was specific, and giving was easy.</p>

                    <h2>Let writing carry the weight</h2>

                    <p>A well-written <a href="/resources/how-to-write-a-missionary-support-letter">support letter</a> does more persuasive work than most conversations, because the reader can absorb it at their own pace, re-read the parts that matter to them, and decide without the social pressure of a live exchange. Put your real effort into the letter: a clear statement of your calling, the specific need, and one obvious next step (a link to give). If the letter is doing its job, some people will give without you ever needing to have a conversation at all.</p>

                    <p>The same logic applies to your ongoing updates. A steady <a href="/resources/free-missionary-newsletter-template">newsletter rhythm</a> keeps supporters connected to your work without requiring you to perform enthusiasm on a call every month. Many introverts find that writing an honest, specific update is far easier — and comes across as more genuine — than a forced check-in phone call.</p>

                    <h2>Reserve conversations for where they actually matter</h2>

                    <p>You don't need to call everyone. Save direct conversations for the relationships where a letter alone genuinely isn't enough — close family, a pastor, a handful of people who would be hurt to only hear from you in writing. For everyone else, a clear letter with a direct ask is a complete, respectful, and effective approach on its own. When you do have a conversation, keep it short and specific rather than trying to "sell" the vision live: state the need plainly, ask directly, and let there be a comfortable silence while the other person thinks. You don't have to fill it.</p>

                    <h2>One-on-one beats group settings — and that's fine</h2>

                    <p>A group presentation is efficient for reaching many people in one sitting, but it isn't a requirement. One-on-one meetings, a coffee with one person at a time, or simply a direct letter followed by a quiet follow-up, work just as well for most individual donors. If the idea of standing in front of a group and asking for money feels impossible, skip it. Build your full support one relationship at a time instead — it takes more individual moments, but each one is smaller and easier to manage than a room.</p>

                    <h2>Let your page do the talking for cold or distant contacts</h2>

                    <p>For extended family, old friends, or acquaintances you're not close enough with to write a personal letter, a clear, well-built support page can do almost all of the work. A page that plainly explains your story and makes giving effortless — especially for recurring gifts — persuades many people on its own, with zero conversation required. <a href="/">Start a page on Devotion Fund</a> and you can send the link directly instead of arranging a call you'd both rather skip.</p>

                    <h2>Protect your energy across a long season</h2>

                    <p>Support raising can take months, and pushing yourself into constant phone calls and events you find draining is a fast way to burn out before you've reached full support. Space your outreach out. Do the writing work in blocks when your energy is highest, and schedule the handful of necessary conversations on days when you have room to recover afterward. A slower, sustainable pace that plays to how you actually communicate will get you to full support more reliably than forcing a style that isn't yours.</p>

                    <p>You don't need a different personality to raise support. You need a clear letter, a page that makes giving effortless, and the handful of direct conversations that truly call for one — everything else is optional.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Can an introvert really raise full missionary support?</strong><br />
                    Yes — most giving decisions happen after someone reads a letter or has one quiet conversation, not because of extroverted energy.</p>

                    <p><strong>Do I have to do phone calls to raise support?</strong><br />
                    No. A clear letter or email converts plenty of people on its own; reserve calls for the relationships that genuinely need one.</p>

                    <p><strong>How do I ask for support without a big group presentation?</strong><br />
                    One-on-one meetings or a direct letter work just as well for most individual donors as a group setting does.</p>

                    <p><strong>Is it okay to let my support page do most of the talking?</strong><br />
                    Yes — a clear page that makes giving effortless does real persuasive work, especially for donors who prefer to decide in their own time.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter That Actually Gets You Funded</a></li>
                        <li><a href="/resources/handling-no-when-asking-for-support">How to Handle a 'No' When Asking for Missionary Support</a></li>
                        <li><a href="/resources/single-missionary-support-raising">Single Missionary Support Raising: 9 Things That Are Different</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We wrote this for the missionaries who've told us the letters and the page felt manageable, but the thought of cold-calling strangers or working a room nearly made them give up on support raising altogether.</p>
                    <p><small><em>Last updated: 2026-07-27.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/introverts-guide-to-support-raising" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
